<template>
  <v-layout
    row
    wrap
    v-if="eventList && eventList.length"
    class="ypo-event-results"
  >
    <v-flex
      v-for="(event, index) in eventList"
      :key="index"
      class="event-card--wrapper"
    >
      <v-card class="event-card">
        <v-card-text>
          <v-layout row wrap>
            <v-flex
              xs12
              class="event-card__title"
              :data-qe-id="`event-result_${index}_title`"
              @click="loadEvent(event)"
            >
              {{event.eventName}}
            </v-flex>

            <v-flex xs12>
              <v-layout column wrap class="event-card__date-location">
                <v-flex
                  class="event-card__date-location--date"
                  :data-qe-id="`event-result-${index}_date`"
                >
                  {{getEventDate(event)}}
                </v-flex>
                <v-flex
                  class="event-card__date-location--day"
                  :data-qe-id="`event-result-${index}_day`"
                >
                  {{getEventDuration(event)}}
                </v-flex>
                <v-flex
                  class="event-card__date-location--location uppercase"
                  :data-qe-id="`event-result-${index}_location`"
                >
                  {{getEventLocation(event)}}
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex xs12>
              <v-layout row wrap class="event-card__image">
                <v-flex
                  xs12
                  :class="[
                    event.isRegistered
                      ? 'event-card__image--registered'
                      : 'event-card__image--register',
                  ]"
                  :data-qe-id="`event-result_${index}_image`"
                >
                  <figure>
                    <v-img
                      :key="event.newEventImageURL"
                      :alt="event.title"
                      :src="sizeFormattedImageUrl(event.newEventImageURL, 500, 750)"
                      :onerror="imageErrorFallback(sizeFormattedImageUrl(event.fallbackEventImageURL, 500, 750))"
                      @click="loadEvent(event, ypoEventConstant.label.EVENT_RESULT_CARD_IMAGE)"
                    />
                  </figure>
                </v-flex>
                <v-flex
                  xs12
                  :class="[
                    'register-btn',
                    event.isRegistered
                      ? 'register-btn--registered'
                      : event.isWaitlisted
                      ? 'register-btn--waitlisted'
                      : 'register-btn--register'
                  ]"
                  :data-qe-id="`event-result_${index}_register`"
                  @click="launchRegisterEvent(event)"
                >
                  <span v-if="event.isRegistered">
                    <i class="fas fa-check"></i>{{ ypoEventConstant.label.REGISTERED }}
                  </span>
                  <span v-else-if="event.isWaitlisted">
                    {{ ypoEventConstant.label.WAITLISTED }}
                  </span>
                  <span v-else>{{ ypoEventConstant.label.REGISTER }}</span>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex
              xs12
              class="event-card__description"
              :data-qe-id="`event-result_${index}_description`"
              v-if="event.shortDescription && event.shortDescription.length"
            >
              {{event.shortDescription}}
            </v-flex>
            <v-flex xs12 class="event-card__topic">
              <span :data-qe-id="`event-result_${index}_topic`">
                {{ truncateEvent(event.focus) }}
              </span>
            </v-flex>
            <v-flex class="event-card__audience"
            :data-qe-id="`event-result_${index}_audience`">
              {{truncateEvent(event.audience)}}
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>
<script>
import {
  getEventDate,
  getEventDuration,
  ypoEventConstant,
  getEventLocation,
} from "@/components/YpoEvents/ypoEvents.constant";
import { imageErrorFallback } from "../../common/utils/utils";
import { gaDataLayerEvent } from '../../plugins/analytics';
import { eventTileConstants, getAttributionTracking } from "@/components/YpoSearch/ypoSearch.constants";
import { gtmEventName } from "@/plugins/gtmAnalytics";
import { sizeFormattedImageUrl } from "@/common/utils/cloudinaryUtils";

export default {
  name: "YpoEventsResult",
  data() {
    return {
      ypoEventConstant,
      sizeFormattedImageUrl,
    };
  },
  props: {
    eventList: {
      type: Array,
      required: true,
      default: () => [],
    },
    mobileAppInterface: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    launchRegisterEvent(event) {
      gaDataLayerEvent(gtmEventName.C_Event_Register_Button, event.eventName);
      this.launchEvent(event);
    },
    loadEvent(event) {
      gaDataLayerEvent(gtmEventName.C_Directory_Event_Selected, event.eventName);
      this.launchEvent(event);
    },
    launchEvent(event) {
      if (event && event.eventUrl) {
        const url = getAttributionTracking(event.eventUrl, eventTileConstants.ATTRIBUTION_TRACKING.eventDirectoryTileId);
        const linkTarget = this.mobileAppInterface ? '_self' : '_blank';
        window.open(url, linkTarget);
      }
    },
    imageErrorFallback,
    getEventDate,
    truncateEvent(data) {
      if (!data) return "";
      return data
        .map(item => {
          return item.includes(">") ? item.replace(">", " | ") : item;
        })
        .join(", ");
    },
    getEventDuration,
    getEventLocation,
  },
};
</script>

<style lang="scss" scoped>
@import "../../common/styles/_colors.scss";
@import "../../common/styles/_responsive.scss";

.ypo-event-results {
  .v-card__text {
    padding: 0;
  }

  .event-card--wrapper {
    box-shadow: 4px 4px 8px 0 $ypo-whisper;
    border: 1px solid $ypo-cobalt-bluish-gray;
    border-radius: 3px;
    margin-bottom: 20px;

    @include breakpointMin(sm) {
      width: 47%;
      max-width: 47%;
      margin-left: 10px;
      margin-right: 10px;
      min-height: 400px;
    }

    @include breakpointMin(lg) {
      width: 31%;
      max-width: 31%;
      margin-right: 20px;
      margin-left: 0;
    }

    @include breakpointMin(xl) {
      width: 23%;
      max-width: 23%;
    }

    @include breakpointMin(xxl) {
      width: 18%;
      max-width: 18%;
    }
  }

  .event-card {
    height: 100%;
    border: 0px;

    &__title {
      color: $ypo-dark-blue;
      cursor: pointer;
      display: -webkit-box;
      font-size: 16px;
      line-height: 21px;
      font-family: 'Raleway' !important;
      font-weight: 500;
      overflow: hidden;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      height: 74px;
      padding: 8px 14px 8px 8px;
      overflow-wrap: break-word;
      &:hover {
        text-decoration: underline !important;
      }

      @include breakpoint(xs) {
        height: auto;
        max-height: 75px;
      }
    }

    &__description {
      color: $black !important;
      font-size: 12px;
      line-height: 15px;
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      padding: 12px 14px 0 8px;
      overflow-wrap: break-word;
    }

    &__topic {
      color: $black !important;
      font-size: 11px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: bold;
      padding: 12px 14px 0 8px;
    }

    &__audience {
      color: $black !important;
      font-size: 11px;
      overflow: hidden;
      padding: 0 14px 8px 8px;
    }

    &__image {
      position: relative;
       figure
       {
          cursor: pointer;
          text-align: center;
          background-color: white;
          line-height: 0px;
          height: auto;
          box-shadow: 0 2px 6px 0 rgba(0,0,0,0.25);

          img {
            width: 100%;
            height: inherit;
          }
        }

      &:hover {

        img {
              transition-property: all;
              transition-duration: 200ms;
          }

      .event-card__image--register {
       img {
              filter: saturate(60%) brightness(60%);
              -webkit-filter: saturate(60%) brightness(60%);
          }
      }

      .event-card__image--registered {
       img {
              filter: saturate(80%) contrast(120%);
              -webkit-filter: saturate(80%) contrast(120%);
              opacity: 0.6;
          }
      }

      .register-btn {
        text-decoration: underline;
        -webkit-box-shadow: 0 0 4px 1px $ypo-white-alpha;
        box-shadow: 0 0 4px 1px $ypo-white-alpha;
        }

      }
    }

    &__date-location {
      background: $ypo-light-bluish-gray;
      padding: 4px 14px 4px 8px;
      white-space: nowrap;
      height: 62px;

      &--date {
        color: $black !important;
        font-size: 13px;
        line-height: 18px;
        font-weight: bold;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &--day {
        color: $black !important;
        font-size: 11px;
        line-height: 15px;
        padding: 0px 0px 6px 0px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &--location {
        color: $ypo-blue;
        font-size: 11px;
        line-height: 15px;
        font-weight: bold;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-height: 13px;
      }
    }

    .register-btn {
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      font-size: 14px;
      font-weight: bold;
      font-family: 'Raleway' !important;
      letter-spacing: 1px;
      line-height: 16px;
      text-transform: uppercase;
      cursor: pointer;
      bottom: 10px;
      right: 10px;
      z-index: 2;
      position: absolute;
      border: 1px solid $ypo-text-border;
      -webkit-box-shadow: 0 0 4px 0 $ypo-white-alpha;
      box-shadow: 0 0 4px 0 $ypo-white-alpha;

      &--register {
        color: $white !important;
        padding: 12px 28px;
        height: auto;
        width: 130px;
        background-color: $ypo-black-opacity;
      }

      &--registered {
        border: 1px solid $ypo-dim-gray;
        background-color: $ypo-white-opac;
        padding: 12px 15px;

        i {
          color: $ypo-green;
          font-size: 14px;
          padding-right: 8px;
        }
      }
      &--waitlisted {
        border: 1px solid $ypo-dim-gray;
        background-color: $ypo-grimmy-grey;
        padding: 12px 15px;
        color: $white;
      }
    }
  }
}
</style>
